/**
 * Pure helpers behind drag-to-zoom on the replicas chart (TRA-1437). A zoom
 * is a time range, two epochs, never row indexes: the usage refresh and a
 * scaling refetch replace the rows, and a range still means the same
 * stretch afterwards. No DOM, so they are testable as they are.
 */

export interface TimeRange {
  start: number
  end: number
}

/** A drag shorter than this much chart time is a click, not a zoom. */
export const MIN_ZOOM_MS = 10 * 60 * 1000

/**
 * The range a drag means, in whichever direction it went, or null when it
 * is too short to be a zoom.
 */
export function dragRange(from: number, to: number): TimeRange | null {
  const start = Math.min(from, to)
  const end = Math.max(from, to)
  return end - start < MIN_ZOOM_MS ? null : { start, end }
}

/** The span the rows cover, or null without rows. The rows are sorted by time. */
export function loadedRange(rows: readonly { epoch: number }[]): TimeRange | null {
  const first = rows[0]
  const last = rows[rows.length - 1]
  if (first === undefined || last === undefined) return null
  return { start: first.epoch, end: last.epoch }
}

/**
 * The rows inside a zoom, with the chart's edges filled in: the last row
 * before the range is carried in at the left edge, because its level holds
 * until the first row inside, and the last row inside is repeated at the
 * right edge, so the step line reaches both edges.
 */
export function zoomRows<T extends { epoch: number }>(rows: readonly T[], zoom: TimeRange): T[] {
  const before = rows.filter((row) => row.epoch < zoom.start)
  const out = rows.filter((row) => row.epoch >= zoom.start && row.epoch <= zoom.end)
  const carried = before[before.length - 1]
  if (carried !== undefined && out[0]?.epoch !== zoom.start) {
    out.unshift({ ...carried, epoch: zoom.start })
  }
  const last = out[out.length - 1]
  if (last !== undefined && last.epoch < zoom.end) out.push({ ...last, epoch: zoom.end })
  return out
}
